import {inject, LOCALE_ID, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {StoreModule} from "@ngrx/store";
import {reducers} from "./app.reducers";
import {StoreDevtoolsModule} from "@ngrx/store-devtools";
import {EffectsModule} from "@ngrx/effects";
import {CommonModule, registerLocaleData} from "@angular/common";
import {TranslateLoader, TranslateModule, TranslateService} from "@ngx-translate/core";
import {TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader} from "@ngx-translate/http-loader";
import localeUa from '@angular/common/locales/uk';
import localeUaExtra from '@angular/common/locales/extra/uk';
import {NgSelectConfig} from "@ng-select/ng-select";
import {ToastrModule} from 'ngx-toastr';
import {NgxDaterangepickerMd} from 'ngx-daterangepicker-material';
import {provideEnvironmentNgxMask} from 'ngx-mask';
import {UserEffects} from '@common/store/effects/user.effects';
import {UserService} from '@common/services/user.service';
import {CoreService} from '@common/services/core.service';
import {CoreEffects} from '@common/store/effects/core.effects';
import {TokenInterceptor} from '@common/interceptors/token.interceptor';
import {RefreshTokenInterceptor} from '@common/interceptors/refresh-token.interceptor.service';
import {ToastrConfig} from '@common/config/config';
import {StarRatingModule} from 'angular-star-rating';
import {OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig} from '@angular/cdk/overlay';

registerLocaleData(localeUa, localeUaExtra);

export function HttpLoaderFactory() {
    return new TranslateHttpLoader();
}

const OVERLAY_CONFIG: OverlayDefaultConfig = {
    usePopover: false,
};

@NgModule({
    declarations: [],
    imports: [
        AppComponent,
        EffectsModule.forRoot([
            UserEffects,
            CoreEffects
        ]),
        StoreModule.forRoot(reducers),
        StoreDevtoolsModule.instrument({maxAge: 25, logOnly: false, connectInZone: true}),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory
            }
        }),
        ToastrModule.forRoot(ToastrConfig),
        NgxDaterangepickerMd.forRoot(),
        StarRatingModule.forRoot(),
        CommonModule,
        BrowserModule,
        AppRoutingModule
    ],
    providers: [
        provideAppInitializer(() => {
            inject(UserService).initializeApp();
            inject(CoreService).setTabId();

            const translate = inject(TranslateService);
            translate.setFallbackLang('ua');
            return translate.use('ua');
        }),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: TRANSLATE_HTTP_LOADER_CONFIG,
            useValue: {
                prefix: './assets/i18n/',
                suffix: '.json?v=' + Date.now()
            }
        },
        provideEnvironmentNgxMask(),
        {
            provide: LOCALE_ID,
            useValue: 'uk'
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RefreshTokenInterceptor,
            multi: true
        },
        {
            provide: NgSelectConfig,
            useFactory: (translate: TranslateService) => {
                const config = new NgSelectConfig();

                translate.get('newTranslate.common_Temp.dahInputTexts.notFoundText').subscribe((translatedText: string) => {
                    config.notFoundText = translatedText;
                });

                translate.get('newTranslate.common_Temp.dahInputTexts.loading').subscribe((translatedText: string) => {
                    config.loadingText = translatedText;
                });

                translate.get('newTranslate.common_Temp.dahInputTexts.typeToSearchText').subscribe((translatedText: string) => {
                    config.typeToSearchText = translatedText;
                });

                config.clearAllText = '';
                return config;
            },
            deps: [TranslateService]
        },
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: OVERLAY_CONFIG }
    ]
})

export class AppModule {
}
